/**
 * annotationStore — les traits d'un calque de dessin, et leur annulation (pur).
 *
 * Le calque de l'ancien moteur (`common/liveMode/liveV3/AnnotationCanvas(.web).js`) gardait ses traits
 * dans une référence que l'écran lui passait (`strokesRef`), pour qu'ils survivent à un remontage et au
 * passage d'une activité à l'autre. Même idée ici : le magasin vit chez l'écran (une clé par activité et
 * par surface), le calque le lit et l'écrit.
 *
 * Ce que l'ancien ne savait pas faire : **la gomme** (`live.md` § 7, n° 10). Gommer retire les traits
 * qu'un geste touche ; l'annulation la remonte (« Annuler » rend les traits gommés), comme elle défait un
 * trait. L'historique dit donc ce qu'a fait chaque geste.
 *
 * « Tout effacer » vide le calque et son historique, comme l'ancien `clearAll` (rien à annuler ensuite).
 */

export type AnnotationPoint = { x: number; y: number }

/** Un trait : ses points (en pixels du calque), sa couleur résolue, son épaisseur. */
export type AnnotationStroke = { points: AnnotationPoint[]; color: string; width: number }

/** Un geste de l'historique : un trait posé, ou un geste de gomme (les traits tels qu'ils étaient avant lui). */
export type AnnotationOp = { kind: "draw" } | { kind: "erase"; before: AnnotationStroke[] }

/** Un geste de gomme en cours : les traits tels qu'ils étaient avant qu'il ne touche le premier. */
export type EraseGesture = { before: AnnotationStroke[] | null }

export function startErase(): EraseGesture {
    return { before: null }
}

/** Le magasin d'un calque. Mutable à dessein : il vit dans une référence de l'écran, comme l'ancien. */
export type AnnotationStore = { strokes: AnnotationStroke[]; history: AnnotationOp[] }

export function createAnnotationStore(): AnnotationStore {
    return { strokes: [], history: [] }
}

/** Pose un trait (un trait d'un seul point se pose aussi : un point). */
export function addStroke(store: AnnotationStore, stroke: AnnotationStroke): void {
    if (stroke.points.length === 0) return
    store.strokes = [...store.strokes, stroke]
    store.history.push({ kind: "draw" })
}

function distanceToSegment(p: AnnotationPoint, a: AnnotationPoint, b: AnnotationPoint): number {
    const dx = b.x - a.x
    const dy = b.y - a.y
    const lengthSquared = dx * dx + dy * dy
    if (lengthSquared === 0) return Math.hypot(p.x - a.x, p.y - a.y)
    const t = Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / lengthSquared))
    return Math.hypot(p.x - (a.x + t * dx), p.y - (a.y + t * dy))
}

/** Le trait passe à moins de `radius` (plus sa demi-épaisseur) du point. */
export function strokeHit(stroke: AnnotationStroke, point: AnnotationPoint, radius: number): boolean {
    const reach = radius + stroke.width / 2
    const { points } = stroke
    if (points.length === 1) return Math.hypot(point.x - points[0].x, point.y - points[0].y) <= reach
    for (let i = 1; i < points.length; i++) {
        if (distanceToSegment(point, points[i - 1], points[i]) <= reach) return true
    }
    return false
}

/**
 * Le geste de la gomme passe par ce point : les traits touchés quittent le calque. Le premier trait touché
 * fait garder au geste l'état d'avant, que `commitErase` range en un seul pas d'annulation.
 */
export function eraseAt(
    store: AnnotationStore,
    point: AnnotationPoint,
    radius: number,
    gesture: EraseGesture
): boolean {
    const kept = store.strokes.filter((stroke) => !strokeHit(stroke, point, radius))
    if (kept.length === store.strokes.length) return false
    if (gesture.before == null) gesture.before = store.strokes
    store.strokes = kept
    return true
}

/** Le geste de gomme fini : il devient un pas d'annulation, s'il a gommé quelque chose. */
export function commitErase(store: AnnotationStore, gesture: EraseGesture): boolean {
    if (gesture.before == null) return false
    store.history.push({ kind: "erase", before: gesture.before })
    gesture.before = null
    return true
}

/** Défait le dernier geste : le dernier trait posé part, ou les traits gommés reviennent. */
export function undoAnnotation(store: AnnotationStore): boolean {
    const last = store.history.pop()
    if (last == null) return false
    if (last.kind === "draw") {
        store.strokes = store.strokes.slice(0, -1)
        return true
    }
    store.strokes = last.before
    return true
}

/** « Tout effacer » : le calque vide, rien à annuler (l'ancien `clearAll`). */
export function clearAnnotation(store: AnnotationStore): void {
    store.strokes = []
    store.history = []
}

/** Ce qu'« Annuler » peut défaire : le nombre de gestes gardés. */
export function annotationSteps(store: AnnotationStore | undefined): number {
    return store?.history.length ?? 0
}
